// Badge system for tracking achievements

export enum BadgeType {
  STREAK = 'streak',
  COMPLETION = 'completion',
  GENIUS = 'genius'
}

export enum BadgeTier {
  NONE = 'none',
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold'
}

export interface Badge {
  type: BadgeType;
  tier: BadgeTier;
  earnedAt?: Date;
}

export interface BadgeRequirements {
  bronze: number;
  silver: number;
  gold: number;
}

// Badge thresholds for each type
export const BADGE_REQUIREMENTS: Record<BadgeType, BadgeRequirements> = {
  [BadgeType.STREAK]: {
    bronze: 3,
    silver: 7,
    gold: 15
  },
  [BadgeType.COMPLETION]: {
    bronze: 0.5,
    silver: 0.75, 
    gold: 1.0 
  },
  [BadgeType.GENIUS]: {
    bronze: 1, 
    silver: 5,
    gold: 10
  }
};

const BADGES_KEY = 'advent_calendar_badges';

interface BadgesData {
  [BadgeType.STREAK]: BadgeTier;
  [BadgeType.COMPLETION]: BadgeTier;
  [BadgeType.GENIUS]: BadgeTier;
}

export const getBadgesData = (): BadgesData => {
  try {
    const stored = localStorage.getItem(BADGES_KEY);
    return stored ? JSON.parse(stored) : {
      [BadgeType.STREAK]: BadgeTier.NONE,
      [BadgeType.COMPLETION]: BadgeTier.NONE,
      [BadgeType.GENIUS]: BadgeTier.NONE
    };
  } catch (error) {
    console.error('Error reading badges data:', error);
    return {
      [BadgeType.STREAK]: BadgeTier.NONE,
      [BadgeType.COMPLETION]: BadgeTier.NONE,
      [BadgeType.GENIUS]: BadgeTier.NONE
    };
  }
};

export const saveBadgesData = (badges: BadgesData): void => {
  try {
    localStorage.setItem(BADGES_KEY, JSON.stringify(badges));
  } catch (error) {
    console.error('Error saving badges data:', error);
  }
};

export const getBadgeTier = (badgeType: BadgeType): BadgeTier => {
  const badges = getBadgesData();
  return badges[badgeType] || BadgeTier.NONE;
};

export const setBadgeTier = (badgeType: BadgeType, tier: BadgeTier): void => {
  const badges = getBadgesData();
  badges[badgeType] = tier;
  saveBadgesData(badges);
};

// Get the tier level for comparison (higher is better)
export const getTierLevel = (tier: BadgeTier): number => {
  switch (tier) {
    case BadgeTier.NONE: return 0;
    case BadgeTier.BRONZE: return 1;
    case BadgeTier.SILVER: return 2;
    case BadgeTier.GOLD: return 3;
    default: return 0;
  }
};

export const getBadgeDisplayName = (type: BadgeType, tier: BadgeTier): string => {
  if (tier === BadgeTier.NONE) {
    return '';
  }

  switch (type) {
    case BadgeType.STREAK:
      switch (tier) {
        case BadgeTier.BRONZE:
          return '3 Day Streak';
        case BadgeTier.SILVER:
          return '7 Day Streak';
        case BadgeTier.GOLD:
          return '15 Day Streak';
        default:
          return '';
      }
    case BadgeType.COMPLETION:
      switch (tier) {
        case BadgeTier.BRONZE:
          return '50% Completion';
        case BadgeTier.SILVER:
          return '75% Completion';
        case BadgeTier.GOLD:
          return '100% Completion';
        default:
          return '';
      }
    case BadgeType.GENIUS:
      switch (tier) {
        case BadgeTier.BRONZE:
          return '1 Day Genius';
        case BadgeTier.SILVER:
          return '5 Day Genius';
        case BadgeTier.GOLD:
          return '10 Day Genius';
        default:
          return '';
      }
    default:
      return '';
  }
};

export const getTierDisplayName = (tier: BadgeTier): string => {
  switch (tier) {
    case BadgeTier.BRONZE:
      return 'Bronze';
    case BadgeTier.SILVER:
      return 'Silver';
    case BadgeTier.GOLD:
      return 'Gold';
    default:
      return '';
  }
};
